/**
 * Free Gift Cart Sync Engine
 * Business Logic:
 * 1. Automatically adds matching Silver product for FREE ($0.00) when a Gold variant (10K Gold, 14K Gold, 18K Gold) is added to cart.
 * 2. Syncs free Silver product quantity with Gold product quantity on cart updates.
 * 3. Automatically removes free Silver product if corresponding Gold product is removed.
 * 4. Prevents purchasing/keeping free Silver product without corresponding Gold product.
 */

window.FreeGiftCart = window.FreeGiftCart || {
  GOLD_MATERIALS: ['10k gold', '14k gold', '18k gold', '10k', '14k', '18k'],
  
  isGoldVariant(variantData) {
    if (!variantData) return false;
    const title = (variantData.title || '').toLowerCase();
    const opt1 = (variantData.option1 || '').toLowerCase();
    const opt2 = (variantData.option2 || '').toLowerCase();
    const opt3 = (variantData.option3 || '').toLowerCase();
    const combined = `${title} ${opt1} ${opt2} ${opt3}`;
    
    const isSilver = (combined.includes('silver') || combined.includes('925')) && !combined.includes('gold');
    if (isSilver) return false;

    return combined.includes('10k') || combined.includes('14k') || combined.includes('18k') || combined.includes('gold');
  },

  isSilverVariant(variantData) {
    if (!variantData) return false;
    const title = (variantData.title || '').toLowerCase();
    const opt1 = (variantData.option1 || '').toLowerCase();
    const opt2 = (variantData.option2 || '').toLowerCase();
    const opt3 = (variantData.option3 || '').toLowerCase();
    const combined = `${title} ${opt1} ${opt2} ${opt3}`;
    
    return (combined.includes('silver') || combined.includes('925')) && !this.isGoldVariant(variantData);
  },

  generateGroupId() {
    return 'grp_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
  },

  findMatchingSilverVariant(variantsList, currentGoldVariant, fallbackSilverId) {
    if (Array.isArray(variantsList) && variantsList.length > 0 && currentGoldVariant) {
      let bestMatch = null;
      let bestScore = -1;

      variantsList.forEach(v => {
        if (!v || !this.isSilverVariant(v)) return;

        let score = 1;
        if (currentGoldVariant.option1 && v.option1 === currentGoldVariant.option1) score += 3;
        if (currentGoldVariant.option2 && v.option2 === currentGoldVariant.option2) score += 3;
        if (currentGoldVariant.option3 && v.option3 === currentGoldVariant.option3) score += 3;

        if (score > bestScore) {
          bestScore = score;
          bestMatch = v;
        }
      });

      if (bestMatch && bestMatch.id) {
        return bestMatch.id;
      }
    }

    return fallbackSilverId || null;
  },

  /**
   * Builds multi-item add payload if selected variant is a Gold variant.
   * If Gold: returns array of items [Gold Item, Silver Free Gift Item].
   * If Silver or other: returns single item array.
   */
  prepareAddPayload(formElement, formData) {
    const goldVariantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity') || '1', 10);
    
    const formNode = formElement.tagName === 'FORM' ? formElement : (formElement.querySelector('form') || formElement);
    const container = formElement.closest('.product-info') || formElement.closest('section') || document.body;

    let variantsList = [];
    const rawMap = formElement.dataset.variantsMap || (formNode && formNode.dataset.variantsMap) || (container && container.dataset.variantsMap);
    
    if (rawMap) {
      try {
        const parsed = JSON.parse(rawMap);
        if (Array.isArray(parsed) && parsed.length > 0) variantsList = parsed;
      } catch(e) {}
    }

    if ((!variantsList || variantsList.length === 0) && window.product && Array.isArray(window.product.variants)) {
      variantsList = window.product.variants;
    }

    if (!variantsList || variantsList.length === 0) {
      const scriptTags = document.querySelectorAll('script[id^="product-variants-"], script[class*="product-json"], script[type="application/json"]');
      for (let script of scriptTags) {
        try {
          const parsed = JSON.parse(script.textContent);
          if (Array.isArray(parsed) && parsed.length > 0 && parsed[0] && parsed[0].id) {
            variantsList = parsed;
            break;
          }
        } catch(e) {}
      }
    }

    const fallbackSilverId = formElement.dataset.silverVariantId || (formNode && formNode.dataset.silverVariantId);
    
    // Check if BOGO offer reward product is present on page
    const bogoElement = document.querySelector('[data-bogo-gift-variant]') || document.getElementById('UnlockGiftBtn');
    const bogoGiftVariantId = (bogoElement && (bogoElement.getAttribute('data-bogo-gift-variant') || bogoElement.getAttribute('data-gift-variant'))) || window.bogoGiftVariantId;

    const currentVariant = variantsList.find(v => v && (v.id == goldVariantId || v.id.toString() === goldVariantId.toString())) || { id: goldVariantId };

    const isGold = this.isGoldVariant(currentVariant);

    // Extract custom properties (e.g. Ring Size, Personalization) for main product
    const baseProperties = {};
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propName = key.replace(/^properties\[/, '').replace(/\]$/, '');
        if (value && value.trim() !== '') {
          baseProperties[propName] = value;
        }
      }
    }

    const groupId = this.generateGroupId();
    const itemsToAdd = [];

    // 1. Add BOGO Reward Product (Earrings) as Free Gift first
    // In Shopify's /cart/add.js, adding the free gift first ensures the main purchased product
    // added subsequent to it lands at index 0 (top of cart), keeping the main item first.
    if (bogoGiftVariantId && bogoGiftVariantId != goldVariantId) {
      // Filter out Ring Size properties for Earrings
      const earringProperties = {};
      for (let k in baseProperties) {
        if (!k.toLowerCase().includes('ring size')) {
          earringProperties[k] = baseProperties[k];
        }
      }
      earringProperties['_is_free_gift'] = 'true';

      if (!itemsToAdd.some(item => item.id === parseInt(bogoGiftVariantId, 10))) {
        itemsToAdd.push({
          id: parseInt(bogoGiftVariantId, 10),
          quantity: quantity,
          properties: earringProperties
        });
      }
    }

    // 2. Main Purchased Item (Ring or any product - Gold or Silver)
    const mainProperties = Object.assign({}, baseProperties, {
      '_group_id': groupId
    });
    itemsToAdd.push({
      id: parseInt(goldVariantId, 10),
      quantity: quantity,
      properties: mainProperties
    });

    if (itemsToAdd.length > 1) {
      return {
        isBundle: true,
        items: itemsToAdd
      };
    }

    // Normal single product addition if no free gifts available
    return {
      isBundle: false,
      items: [
        {
          id: parseInt(goldVariantId, 10),
          quantity: quantity,
          properties: baseProperties
        }
      ]
    };
  },

  /**
   * Syncs free gifts in cart.
   * If a Gold/Silver product is removed or quantity updated, syncs paired gift item(s).
   */
  async syncCart(cartState) {
    if (!cartState || !cartState.items) return null;
    
    const items = cartState.items;
    const updates = {};
    let needsUpdate = false;
    // Check if there is any regular item (not a free gift)
    const hasRegularItem = items.some(item => {
      const props = item.properties || {};
      return props._is_free_gift !== 'true' && props._is_free_gift !== true;
    });

    const freeGifts = items.filter(item => {
      const props = item.properties || {};
      return props._is_free_gift === 'true' || props._is_free_gift === true;
    });

    if (hasRegularItem) {
      // Keep exactly 1 free gift item with quantity 1
      freeGifts.forEach((gift, idx) => {
        if (idx === 0) {
          if (gift.quantity !== 1) {
            updates[gift.key] = 1;
            needsUpdate = true;
          }
        } else {
          // Remove duplicate free gift line items
          updates[gift.key] = 0;
          needsUpdate = true;
        }
      });
    } else {
      // Remove all free gifts if cart is empty of regular items
      freeGifts.forEach(gift => {
        if (gift.quantity !== 0) {
          updates[gift.key] = 0;
          needsUpdate = true;
        }
      });
    }

    if (!needsUpdate) return null;

    const isCartPage = window.location.pathname.includes('/cart');
    const secToInclude = isCartPage
      ? ['cart-section', 'ajax-cart-icon', 'cart-section-total']
      : ['ajax-cart', 'ajax-cart-icon'];

    const body = JSON.stringify({
      updates,
      sections: secToInclude,
      sections_url: window.location.pathname
    });

    try {
      const res = await fetch(`${window.routes.change_cart_url || '/cart/update.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body
      });
      const data = await res.json();
      if (data && data.sections && data.sections['ajax-cart']) {
        const ajaxCartItems = document.getElementById('ajaxcart-cartitems');
        if (ajaxCartItems) {
          const parsed = new DOMParser().parseFromString(data.sections['ajax-cart'], 'text/html');
          const newItems = parsed.querySelector('#ajaxcart-cartitems');
          if (newItems) ajaxCartItems.innerHTML = newItems.innerHTML;
        }
      }
      return data;
    } catch(e) {
      console.error('[FreeGiftCart] Cart sync failed:', e);
      return null;
    }
  }
};

(function() {
  let isSyncing = false;
  function runSync() {
    if (isSyncing) return;
    isSyncing = true;
    fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart.js' : '/cart.js')
      .then(r => r.json())
      .then(cart => {
        if (window.FreeGiftCart && window.FreeGiftCart.syncCart) {
          return window.FreeGiftCart.syncCart(cart);
        }
      })
      .finally(() => {
        isSyncing = false;
      });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runSync);
  } else {
    runSync();
  }

  // Hook into plugins.js event
  const checkSpObject = setInterval(() => {
    if (typeof user === 'function' && typeof SP_OBJECT !== 'undefined') {
      clearInterval(checkSpObject);
      user(SP_OBJECT.ajxcartRefresh, runSync);
    }
  }, 500);

  // Hook into fetch API for cart/add
  const originalFetch = window.fetch;
  window.fetch = function() {
    const fetchPromise = originalFetch.apply(this, arguments);
    const url = arguments[0] || '';
    if (typeof url === 'string' && url.includes('/cart/add')) {
      fetchPromise.then(res => {
        setTimeout(runSync, 500);
        return res;
      });
    }
    return fetchPromise;
  };
})();
